import { useState } from 'react';

import amara from '@/assets/field-personas/amara.webp';
import elena from '@/assets/field-personas/elena.webp';
import jordan from '@/assets/field-personas/jordan.jpeg';
import marcus from '@/assets/field-personas/marcus.jpeg';
import noor from '@/assets/field-personas/noor.jpeg';
import { cn } from '@/shared/lib/cn';

import type { FieldActorId } from '@mio/shared';

import './field-persona-avatar.css';

interface Portrait {
  src: string;
  width: number;
  height: number;
  crop: { x: number; y: number; size: number };
}

// Square face-and-shoulder windows in each original photo; the source files stay intact.
const portraits: Record<FieldActorId, Portrait> = {
  jordan: { src: marcus, width: 256, height: 384, crop: { x: 64, y: 12, size: 128 } },
  noor: { src: noor, width: 447, height: 447, crop: { x: 135, y: 45, size: 245 } },
  elena: { src: elena, width: 704, height: 940, crop: { x: 100, y: 40, size: 530 } },
  marcus: { src: jordan, width: 380, height: 526, crop: { x: 83, y: 45, size: 226 } },
  amara: { src: amara, width: 500, height: 500, crop: { x: 125, y: 25, size: 260 } },
};

function PortraitImage({ portrait, initials }: { portrait: Portrait; initials: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return initials;
  return (
    <img
      src={portrait.src}
      alt=""
      width={portrait.width}
      height={portrait.height}
      decoding="async"
      draggable={false}
      onError={() => setFailed(true)}
      style={{
        width: `${(portrait.width / portrait.crop.size) * 100}%`,
        left: `${(-portrait.crop.x / portrait.crop.size) * 100}%`,
        top: `${(-portrait.crop.y / portrait.crop.size) * 100}%`,
      }}
    />
  );
}

/** Decorative beside the colleague's visible name; initials remain available on image failure. */
export function FieldPersonaAvatar({
  actorId,
  name,
  className,
}: {
  actorId: FieldActorId;
  name: string;
  className?: string;
}) {
  const portrait = portraits[actorId];
  const initials = name
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join('');
  return (
    <span
      className={cn('field-persona-avatar', className)}
      aria-hidden="true"
      data-persona-avatar={actorId}
    >
      {portrait ? (
        <PortraitImage key={actorId} portrait={portrait} initials={initials} />
      ) : (
        initials
      )}
    </span>
  );
}
